// src/pages/SharedNote.jsx
// 3.77.0 (owner, Nov 2): a note from My notes shared with an email address
// that has no Fax Portal login. The email's link is …/portal/?shared-note=1#t=<token>
// (the token stays after the #, so it never reaches a server log). Nothing is
// sent until the person asks: "Email me a code" sends a 6-digit code to the
// shared address (10 minutes); the right code lets this browser open the
// note for 12 hours (a pass, kept in this browser only), then a new code is
// needed. With "Can edit" they change the title and text or items and Save.
// Stopping sharing ends the link and the pass at once (backend
// lib/noteShares.js, routes/sharedNote.js).
//
// 3.77.3 (owner): changes the owner (or anyone else) makes show here by
// themselves: checked every 15 seconds while the page is in front, and at
// once when it comes back. Nothing typed here is ever replaced: with
// unsaved changes the page says the note changed and offers Show the latest.
import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../firebase/config';
import AuthShell, { AuthMessage } from '../components/AuthShell';
import { S } from './ChooseOwnPassword';
import { RichText } from '../components/EmailEditorParts';
import { cleanPaste, fromPlain, toText } from '../utils/coverMessage';

export function sharedNoteLinkFromLocation() {
  const q = new URLSearchParams(window.location.search);
  if (!q.get('shared-note')) return null;
  const t = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('t') || '';
  return { t };
}

const BASE = `${API_BASE}/portal/shared-note`;
const passKey = t => `faxPortal.notePass.${t.slice(0, 16)}`;
const readPass = t => { try { const p = JSON.parse(localStorage.getItem(passKey(t)) || 'null'); return p && Date.parse(p.until) > Date.now() ? p.pass : null; } catch { return null; } };
const keepPass = (t, pass, hours) => { try { localStorage.setItem(passKey(t), JSON.stringify({ pass, until: new Date(Date.now() + hours * 3600e3).toISOString() })); } catch { /* this browser keeps nothing: a code each time */ } };
const dropPass = t => { try { localStorage.removeItem(passKey(t)); } catch { /* fine */ } };
async function call(path, { method = 'GET', body, pass } = {}) {
  const r = await fetch(`${BASE}${path}`, { method, headers: { 'Content-Type': 'application/json', ...(pass ? { Authorization: `Pass ${pass}` } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || 'Something went wrong. Please try again.'), { status: r.status, data: j });
  return j;
}
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

export default function SharedNote({ link }) {
  const [state, setState] = useState(link.t ? 'checking' : 'problem');   // checking | start | code | note | problem
  const [info, setInfo] = useState({ message: link.t ? '' : 'This link isn’t complete. Please open it again from your email.' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [view, setView] = useState(null);       // { note, can, ownerName, email }
  const [draft, setDraft] = useState(null);     // the note being changed (Can edit)
  const [rev, setRev] = useState(0);
  const [saved, setSaved] = useState('');
  const [newer, setNewer] = useState(null);      // a newer version waiting while there are unsaved changes
  const loaded = useRef('');                     // the draft as last loaded, to tell unsaved changes
  const asDraft = v => ({ title: v.note.title || '', html: v.note.html || fromPlain(v.note.text), items: v.note.items || [] });

  const show = v => { const d = asDraft(v); loaded.current = JSON.stringify(d); setView(v); setDraft(d); setNewer(null); setRev(r => r + 1); setState('note'); };
  const viewRef = useRef(null); viewRef.current = view;
  const draftRef = useRef(null); draftRef.current = draft;
  // 3.77.3: take in changes made elsewhere
  useEffect(() => {
    if (state !== 'note') return undefined;
    let busy = false;
    const check = () => {
      const pass = readPass(link.t);
      if (document.hidden || busy || !pass) return;
      busy = true;
      call('', { pass }).then(v => {
        const cur = viewRef.current;
        if (!cur || !(v.note.updatedAt > cur.note.updatedAt)) return;
        if (JSON.stringify(draftRef.current) === loaded.current) { show(v); setSaved(''); }
        else setNewer(v);   // they're typing: never replace it
      }).catch(e => {
        if (e.status === 404) { dropPass(link.t); setInfo({ message: e.message }); setState('problem'); }
        else if (e.status === 401) { dropPass(link.t); setErr('Your 12 hours are up. Please ask for a new code.'); setState('start'); }
      }).finally(() => { busy = false; });
    };
    const tick = setInterval(check, 15000);
    const onBack = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', onBack); window.removeEventListener('focus', onBack); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, link.t]);
  useEffect(() => {
    if (!link.t) return;
    const pass = readPass(link.t);
    if (!pass) { setState('start'); return; }
    call('', { pass }).then(show).catch(e => { dropPass(link.t); if (e.status === 404) { setInfo({ message: e.message }); setState('problem'); } else setState('start'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link.t]);

  async function sendCode() {
    setBusy(true); setErr('');
    try { const j = await call('/code', { method: 'POST', body: { t: link.t } }); setInfo(j); setState('code'); }
    catch (e) { if (e.status === 404) { setInfo({ message: e.message }); setState('problem'); } else setErr(e.message); }
    setBusy(false);
  }
  async function verify(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const j = await call('/verify', { method: 'POST', body: { t: link.t, code } });
      keepPass(link.t, j.pass, j.hours);
      show(await call('', { pass: j.pass }));
    } catch (x) { if (x.status === 404) { setInfo({ message: x.message }); setState('problem'); } else setErr(x.message); }
    setBusy(false);
  }
  async function save() {
    setBusy(true); setErr(''); setSaved('');
    const pass = readPass(link.t);
    const body = { title: draft.title, base: view.note.updatedAt, ...(view.note.kind === 'list' ? { items: draft.items } : { html: draft.html, text: toText(draft.html) }) };
    try { show(await call('', { method: 'PUT', body, pass })); setSaved('Saved.'); }
    catch (x) {
      if (x.status === 409 && x.data?.note) { show(x.data); setErr('Someone else changed this note just now. You’re seeing their latest version; please make your change again.'); }
      else if (x.status === 401 || !pass) { dropPass(link.t); setState('start'); setErr('Your 12 hours are up. Please ask for a new code.'); }
      else setErr(x.message);
    }
    setBusy(false);
  }

  const edit = view?.can === 'edit';
  const setItem = (id, patch) => setDraft(d => ({ ...d, items: d.items.map(i => (i.id === id ? { ...i, ...patch } : i)) }));
  return (
    <AuthShell stack wide={state === 'note'}>
      <h1 style={S.title}>{state === 'note' ? (view.note.title || 'Shared note') : 'A note shared with you'}</h1>
      {state === 'checking' && <p style={S.text}>Checking your link…</p>}
      {state === 'start' && <>
        <p style={S.text}>To check it’s you, we’ll email a 6-digit code to the address this note was shared with.</p>
        {err && <AuthMessage>{err}</AuthMessage>}
        <button onClick={sendCode} disabled={busy} className="auth-btn">{busy ? 'Sending…' : 'Email me a code'}</button>
      </>}
      {state === 'code' && <form onSubmit={verify} style={S.form}>
        <p style={S.text}>{info.ownerName ? `${info.ownerName} shared this note. ` : ''}We emailed a code to <strong>{info.to}</strong>. It works for {info.minutes || 10} minutes.</p>
        <label style={S.label}>Code
          <input value={code} onChange={e => setCode(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))} inputMode="numeric" autoComplete="one-time-code" autoFocus style={{ ...S.input, letterSpacing: 4, fontSize: 20 }} />
        </label>
        {err && <AuthMessage>{err}</AuthMessage>}
        <button type="submit" disabled={busy || code.replace(/\D/g, '').length !== 6} className="auth-btn">{busy ? 'Checking…' : 'Open the note'}</button>
        <button type="button" onClick={sendCode} disabled={busy} style={{ ...S.link, background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start', padding: 0 }}>Send a new code</button>
      </form>}
      {state === 'note' && <>
        <p style={{ ...S.text, fontSize: 13 }}>Shared by <strong>{view.ownerName || 'someone'}</strong> · {edit ? 'you can view and change it' : 'you can view it'} · you’re signed in to this note on this browser until {new Date(view.expiresAt).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })} (the code was only for signing in)</p>
        {edit && <label style={S.label}>Title<input value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value.slice(0, 120) }))} style={S.input} /></label>}
        {view.note.kind === 'list' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {draft.items.map(i => (
              <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={!!i.done} disabled={!edit} onChange={() => setItem(i.id, { done: !i.done })} style={{ width: 18, height: 18 }} />
                {edit ? <input value={i.text} onChange={e => setItem(i.id, { text: e.target.value.slice(0, 500) })} style={{ ...S.input, padding: '8px 10px', textDecoration: i.done ? 'line-through' : 'none' }} />
                  : <span style={{ ...S.text, textDecoration: i.done ? 'line-through' : 'none' }}>{i.text}</span>}
              </label>
            ))}
            {edit && <button type="button" onClick={() => setDraft(d => ({ ...d, items: [...d.items, { id: newId(), text: '', done: false }] }))} style={{ ...S.link, background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start', padding: 0 }}>＋ Add item</button>}
          </div>
        ) : edit ? (
          <RichText key={rev} initial={draft.html} onChange={html => setDraft(d => ({ ...d, html }))} placeholders={false} pictures={false} links={false} cleanPaste={cleanPaste} minHeight={220} />
        ) : (
          <div className="et-rich" style={{ ...S.text, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: view.note.html || fromPlain(view.note.text) }} />
        )}
        {err && <AuthMessage>{err}</AuthMessage>}
        {saved && <AuthMessage tone="success">{saved}</AuthMessage>}
        {newer && <AuthMessage tone="info">{newer.ownerName || 'Someone'} changed this note. <button type="button" onClick={() => show(newer)} style={{ ...S.link, background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', fontWeight: 700 }}>Show the latest</button> (your changes here that aren’t saved will be lost)</AuthMessage>}
        {edit && <button onClick={save} disabled={busy} className="auth-btn">{busy ? 'Saving…' : 'Save changes'}</button>}
      </>}
      {state === 'problem' && <AuthMessage>{info.message || 'This link isn’t valid any more.'}</AuthMessage>}
    </AuthShell>
  );
}
